/* eslint-disable no-param-reassign */
import cx from 'classnames';
import React from 'react';
import { toast } from 'sonner';

import { makeStyles, useMediaQuery } from '@material-ui/core';

import DataFilterControls from 'components/DataMap/DataFilterControls';
import EventDataDetailsDrawer from 'components/DataMap/EventDataDetailsDrawer';
import EventMarkers from 'components/DataMap/EventMarkers';
import { useMapInfoContext } from 'components/DataMap/MapInfoProvider';
import ZipcodePolygon from 'components/DataMap/ZipcodePolygon';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';

import { getZipcodesByBounds } from 'utils/api';
import { showAPIErrorAlert } from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  loadingIndicator: {
    position: 'absolute',
    marginTop: '10px',
    marginLeft: '10px',
    top: '0px',
    left: '0px',
    zIndex: 100,
  },
  zipcodeHoverLabel: {
    position: 'absolute',
    top: '12px',
    left: '12px',
    zIndex: 101,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '7px 11px',
    border: `1px solid ${theme.palette.grayScale6.main}`,
    borderRadius: '999px',
    pointerEvents: 'none',
    color: theme.palette.grayScale9.main,
    backgroundColor: theme.palette.white.main,
    boxShadow: '0 2px 8px rgba(23, 23, 24, 0.18)',
    fontSize: '13px',
    lineHeight: 1.2,
    opacity: 1,
    visibility: 'visible',
    transform: 'translateY(0)',
    transition: 'opacity 120ms ease, transform 120ms ease, visibility 120ms ease',

    '@media (hover: none), (pointer: coarse)': {
      display: 'none',
    },

    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  zipcodeHoverLabelHidden: {
    opacity: 0,
    visibility: 'hidden',
    transform: 'translateY(-4px)',
  },
  zipcodeHoverLabelPrefix: {
    color: theme.palette.lightGray.main,
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  zipcodeHoverLabelValue: {
    fontWeight: 700,
  },
}));

const zoomThresholdToShowZipcodes = 11;

function ZipcodeAndDataOverlay({
  highlightZipcodes,
  clickToLoadZipcodeEventCounts,
  showOnlyHighlightedZipcodes,
  highlightEventData,
  fetchHighlightEventDataByBounds,
  enableEventDataDetails,
  isDrawingShape,
  initPropertyFilters,
}) {
  const classes = useStyles();
  const canHover = useMediaQuery('(hover: hover) and (pointer: fine)');
  const { mapInstance, mapBounds } = useMapInfoContext();

  const [forceRefetchData, setForceRefetchData] = React.useState(null); // Used to force data fetching
  const [isLoading, setIsLoading] = React.useState(false);

  const [loadedZipcodeMapBounds, setLoadedZipcodeMapBounds] = React.useState(null);
  const [loadedHighlightEventDataMapBounds, setLoadedHighlightEventDataMapBounds] = React.useState(null);

  const [loadedZipcodes, setLoadedZipcodes] = React.useState(null);
  const [loadedHighlightEventData, setLoadedHighlightEventData] = React.useState([]);
  const [selectedEventData, setSelectedEventData] = React.useState(null);
  const [hoveredZipcode, setHoveredZipcode] = React.useState(null);
  const [lastHoveredZipcode, setLastHoveredZipcode] = React.useState(null);
  const highlightEventDataLoadingToastIdRef = React.useRef(null);
  const showStationaryZipcodeLabel = enableEventDataDetails && canHover;

  const onEventDataSelect = React.useCallback((eventData) => {
    if (enableEventDataDetails) setSelectedEventData(eventData);
  }, [enableEventDataDetails]);

  const onZipcodeHoverChange = React.useCallback((zipcode) => {
    if (zipcode) setLastHoveredZipcode(zipcode);
    setHoveredZipcode(zipcode);
  }, []);

  React.useEffect(() => {
    setHoveredZipcode(null);
  }, [mapBounds, showStationaryZipcodeLabel]);

  React.useEffect(() => {
    if (!selectedEventData) return;

    const availableEventData = [
      ...loadedHighlightEventData,
      ...(highlightEventData || []),
    ];
    const selectedEventStillExists = availableEventData.some(({ _id }) => String(_id) === String(selectedEventData._id));

    if (!enableEventDataDetails || !selectedEventStillExists || mapInstance?.zoom < zoomThresholdToShowZipcodes) {
      setSelectedEventData(null);
    }
  }, [enableEventDataDetails, highlightEventData, loadedHighlightEventData, mapBounds, mapInstance, selectedEventData]);

  React.useEffect(() => {
    if (!mapInstance) return;

    if (mapInstance?.zoom < zoomThresholdToShowZipcodes) {
      return;
    }

    // Don't update the bounds and requery for zipcodes if the new bounds are within the currently loaded ones
    if (loadedZipcodeMapBounds
        && loadedZipcodeMapBounds.contains(mapInstance.getBounds().getNorthEast())
        && loadedZipcodeMapBounds.contains(mapInstance.getBounds().getSouthWest())) {
      return;
    }

    setLoadedZipcodeMapBounds(mapInstance.getBounds());
    setForceRefetchData(Date.now());
  }, [mapBounds]);

  React.useEffect(() => {
    (async () => {
      if (!mapInstance) return;
      if (!loadedZipcodeMapBounds) return;

      const boundsNe = {
        lat: mapInstance.getBounds().getNorthEast().lat(),
        lng: mapInstance.getBounds().getNorthEast().lng(),
      };
      const boundsSw = {
        lat: mapInstance.getBounds().getSouthWest().lat(),
        lng: mapInstance.getBounds().getSouthWest().lng(),
      };

      try {
        setIsLoading(true);

        const { zipcodes } = await getZipcodesByBounds(boundsNe, boundsSw);

        setLoadedZipcodes(zipcodes);
      } catch (err) {
        console.error(err);
        showAPIErrorAlert(err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [forceRefetchData]);

  React.useEffect(() => {
    if (!mapInstance || !fetchHighlightEventDataByBounds || mapInstance?.zoom < zoomThresholdToShowZipcodes) {
      setLoadedHighlightEventData([]);
      setLoadedHighlightEventDataMapBounds(null);
      if (highlightEventDataLoadingToastIdRef.current != null) {
        toast.dismiss(highlightEventDataLoadingToastIdRef.current);
        highlightEventDataLoadingToastIdRef.current = null;
      }
      return undefined;
    }

    if (loadedHighlightEventDataMapBounds
        && loadedHighlightEventDataMapBounds.contains(mapInstance.getBounds().getNorthEast())
        && loadedHighlightEventDataMapBounds.contains(mapInstance.getBounds().getSouthWest())) {
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        const bounds = mapInstance.getBounds();
        if (!bounds) return;
        highlightEventDataLoadingToastIdRef.current = toast.loading('Loading farmed APNs...');

        const boundsNe = {
          lat: bounds.getNorthEast().lat(),
          lng: bounds.getNorthEast().lng(),
        };
        const boundsSw = {
          lat: bounds.getSouthWest().lat(),
          lng: bounds.getSouthWest().lng(),
        };

        const { eventData } = await fetchHighlightEventDataByBounds(boundsNe, boundsSw);
        setLoadedHighlightEventData(eventData || []);
        setLoadedHighlightEventDataMapBounds(bounds);
      } catch (err) {
        console.error(err);
        showAPIErrorAlert(err);
      } finally {
        if (highlightEventDataLoadingToastIdRef.current != null) {
          toast.dismiss(highlightEventDataLoadingToastIdRef.current);
          highlightEventDataLoadingToastIdRef.current = null;
        }
      }
    }, 350);

    return () => {
      window.clearTimeout(timeoutId);
      if (highlightEventDataLoadingToastIdRef.current != null) {
        toast.dismiss(highlightEventDataLoadingToastIdRef.current);
        highlightEventDataLoadingToastIdRef.current = null;
      }
    };
  }, [fetchHighlightEventDataByBounds, mapBounds, mapInstance]);

  if (mapInstance?.zoom < zoomThresholdToShowZipcodes) {
    return null;
  }

  if (!loadedZipcodes) {
    return null;
  }

  return (
    <>
      {isLoading && (
        <div className={classes.loadingIndicator}>
          <LoadingIndicator />
        </div>
      )}
      <DataFilterControls initPropertyFilters={initPropertyFilters} />
      {showStationaryZipcodeLabel && lastHoveredZipcode && (
        <div
          className={cx(classes.zipcodeHoverLabel, !hoveredZipcode && classes.zipcodeHoverLabelHidden)}
          aria-hidden={!hoveredZipcode}
        >
          <span className={classes.zipcodeHoverLabelPrefix}>ZIP</span>
          <span className={classes.zipcodeHoverLabelValue}>{lastHoveredZipcode}</span>
        </div>
      )}
      {loadedZipcodes.map(
        ({
          zipcode, zipcodePolygons,
        }) => {
          // Check if we only want to show highlighted zipcodes
          if (showOnlyHighlightedZipcodes && highlightZipcodes && !highlightZipcodes.includes(zipcode)) return null;

          return (
            <ZipcodePolygon
              key={zipcode}
              zipcode={zipcode}
              zipcodePolygons={zipcodePolygons}
              highlight={highlightZipcodes && highlightZipcodes.includes(zipcode)}
              clickToLoadZipcodeEventCounts={clickToLoadZipcodeEventCounts}
              isDrawingShape={isDrawingShape}
              disableInteractions={enableEventDataDetails}
              onHoverChange={showStationaryZipcodeLabel ? onZipcodeHoverChange : undefined}
            />
          );
        },
      )}
      {fetchHighlightEventDataByBounds && loadedHighlightEventData.length > 0 && (
        <EventMarkers
          eventData={loadedHighlightEventData}
          ignoreZoomThresholdCulling
          highlightEventDataApns={loadedHighlightEventData}
          onEventDataSelect={enableEventDataDetails ? onEventDataSelect : undefined}
          selectedEventDataId={selectedEventData?._id}
        />
      )}
      {/* Render outside-provided & highlighted event data separately and always show it */}
      {highlightEventData && highlightEventData.length > 0 && (
        <EventMarkers
          eventData={highlightEventData}
          ignoreZoomThresholdCulling
          highlightEventDataApns={highlightEventData}
          onEventDataSelect={enableEventDataDetails ? onEventDataSelect : undefined}
          selectedEventDataId={selectedEventData?._id}
        />
      )}
      <EventDataDetailsDrawer eventData={selectedEventData} onClose={() => setSelectedEventData(null)} />
    </>
  );
}

export default ZipcodeAndDataOverlay;
